import React, { useState, useEffect, useContext } from 'react';
import { Laugh, Smile, Meh, Frown, ArrowLeft, ArrowRight, MoveUp } from 'lucide-react';
import CalculateRequiredIncomeToAfford from '/src/services/CalculateRequiredIncomeToAfford';
import LTVCalculators from '/src/services/LTVCalculators';
import { AreaDataContext } from '/src/Views/PropertyExplorer/PropertyExplorer';
import LTVAttractivenessAbout from './Stages/About/LTVAttractivenessAbout';
import LTVAttractivenessLookingAtMarket from './Stages/LookingAtLocalMarket/LTVAttractivenessLookingAtMarket';
import LTVAttractivenessIncomeRequiredToday from './Stages/IncomeRequiredToday/LTVAttractivenessIncomeRequiredToday';
import LTVAttractivenessFutureLooking from './Stages/FutureGapToLocalMarket/LTVAttractivenessFutureLooking';
import './style.css';
import { AddressSearchContext } from '/src/App';
import { usePropertyGaLogging } from '/src/contexts/PropertyGaLoggingContext';
// import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
// import CalculateMarketGrowthRates from '/src/services/CalculateMarketGrowthRates';
// import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
// import { Doughnut, Line } from 'react-chartjs-2';
// import HomeValueVsRequiredIncomeChart from './HomeValueVsRequiredIncomeChart/HomeValueVsRequiredIncomeChart';
// import ProjectedAreaIncomeChart from './ProjectedAreaIncomeChart/ProjectedAreaIncomeChart';


const formatHouseholdsNumber = (number) => {
    if (number >= 1000000) {
        return `${(number / 1000000).toFixed(1)}M`;
    } else if (number >= 1000) {
        return `${(number / 1000).toFixed(1)}K`;
    }
    return number;
}

const LTVAttractiveness = ({
    ltvInfo,
    originalValues
}) => {
    // const { LOG_GA_EVENT } = useContext(AddressSearchContext);
    const { handleLoggingGaEventFromProperty } = usePropertyGaLogging();
    const { geoData } = useContext(AreaDataContext);

    const fipsData = geoData.fips || {};
    const zipData = geoData.zip || {};

    const fipsTotalHouseholds = fipsData.census_income_data?.['2023']?.total_households || 0;
    const zipTotalHouseholds = zipData.census_income_data?.['2023']?.total_households || 0;

    const formattedFipsTotalHouseholds = fipsTotalHouseholds !== 0 ? formatHouseholdsNumber(fipsTotalHouseholds) : null;
    const formattedZipTotalHouseholds = zipTotalHouseholds !== 0 ? formatHouseholdsNumber(zipTotalHouseholds) : null;

    // ----------------- GET THE KEY DATA ------------------------------------------------------------------------------------------------

    const county_name = originalValues.countyName
    const zip_code = originalValues.zipCode

    const zipChartData = originalValues.zipChartData
    const fipsChartData = originalValues.fipsChartData

    const incomeLevelCalculations = originalValues.incomeLevelCalculations

    const highest_income_level = originalValues.highestIncomeLevel

    const current_monthly_expenses = originalValues.currentMonthlyExpenses

    const highest_income_today_calculated = highest_income_level.value_today

    const percentageLevels = [
        { percentage: .30, level: 'best', icon: Laugh },
        { percentage: .35, level: 'good', icon: Smile },
        { percentage: .40, level: 'ok', icon: Meh },
        { percentage: .45, level: 'bad', icon: Frown }
    ];

    const original_income_gaps = originalValues.incomeGaps

    const stage3_income_display_array = [
        {
            location: county_name,
            total_households: formattedFipsTotalHouseholds,
            chart_data: fipsChartData,
            income_data: [incomeLevelCalculations['fips-median'], incomeLevelCalculations['fips-mean']]
        },
        {
            location: `ZIP ${zip_code}`,
            total_households: formattedZipTotalHouseholds,
            chart_data: zipChartData,
            income_data: [incomeLevelCalculations['zip-median'], incomeLevelCalculations['zip-mean']]
        }
    ]

    // ----------------- CREATE THE STATE VARIABLES UTILIZING THE ORIGINAL VALUES ------------------------------------------------------------------------------------------------

    // States for the assumptions
    // const [showAssumptions, setShowAssumptions] = useState(false);
    // const [incomeSpentOnHousingPercent, setIncomeSpentOnHousingPercent] = useState(originalValues.incomeSpentOnHousing);
    const [currentStage, setCurrentStage] = useState(1);
    const [currentHomeValue, setCurrentHomeValue] = useState(originalValues.currentHomeValue);
    const [downPaymentPercent, setDownPaymentPercent] = useState(originalValues.downPayment);
    const [futureHomeValues, setFutureHomeValues] = useState(originalValues.futureHomeValues);
    const [futureIncomeRequired, setFutureIncomeRequired] = useState(null);
    const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false);
    const [incomeGaps, setIncomeGaps] = useState(null);
    const [interestRatePercent, setInterestRatePercent] = useState(originalValues.interestRate);
    const [marketGrowthRate, setMarketGrowthRate] = useState(originalValues.marketGrowthRate);
    const [projectedFutureIncome, setProjectedFutureIncome] = useState(originalValues.projectedFutureIncome);
    const [userIncomeGrowthRate, setUserIncomeGrowthRate] = useState(null);

    // Calculate required income for each of the income percentages
    const requiredIncome30 = CalculateRequiredIncomeToAfford(currentHomeValue, current_monthly_expenses, interestRatePercent, .30, downPaymentPercent);
    const requiredIncome35 = CalculateRequiredIncomeToAfford(currentHomeValue, current_monthly_expenses, interestRatePercent, .35, downPaymentPercent);
    const requiredIncome40 = CalculateRequiredIncomeToAfford(currentHomeValue, current_monthly_expenses, interestRatePercent, .40, downPaymentPercent);
    const requiredIncome45 = CalculateRequiredIncomeToAfford(currentHomeValue, current_monthly_expenses, interestRatePercent, .45, downPaymentPercent);

    // Comparison constants
    const incomeHigherInCounty = originalValues.incomeHigherInCounty

    const medianIncomeGrowingFasterInCounty = originalValues.medianIncomeGrowingFasterInCounty

    const meanIncomeGrowingFasterInCounty = originalValues.meanIncomeGrowingFasterInCounty

    const allRequiredIncomesAbove200k = [
        Number(requiredIncome30),
        Number(requiredIncome35),
        Number(requiredIncome40),
        Number(requiredIncome45)
    ].every(income => income > 200000);

    const incomePercentages = [
        { icon: Frown, percentage: '45%', level: 'bad', requiredIncome: requiredIncome45 },
        { icon: Meh, percentage: '40%', level: 'ok', requiredIncome: requiredIncome40 },
        { icon: Smile, percentage: '35%', level: 'good', requiredIncome: requiredIncome35 },
        { icon: Laugh, percentage: '30%', level: 'best', requiredIncome: requiredIncome30 }
    ];

    const handleReset = () => {
        setCurrentHomeValue(originalValues.currentHomeValue);
        setInterestRatePercent(originalValues.interestRate);
        setDownPaymentPercent(originalValues.downPayment);
        // setIncomeSpentOnHousingPercent(originalValues.incomeSpentOnHousing);
        setMarketGrowthRate(originalValues.marketGrowthRate);
        setFutureHomeValues(originalValues.futureHomeValues);
        // setShowAssumptions(false);
        setUserIncomeGrowthRate(null);
        setProjectedFutureIncome(originalValues.projectedFutureIncome);
        setIncomeGaps(null);
    };

    // ----------------- THE USEEFFECTS ------------------------------------------------------------------------------------------------

    // useEffect that updates the future home values when the currentHomeValue or marketGrowthRate changes
    useEffect(() => {
        const newFutureHomeValues = LTVCalculators.calculate_future_home_value(currentHomeValue, marketGrowthRate);
        setFutureHomeValues(newFutureHomeValues);
    }, [currentHomeValue, marketGrowthRate]);

    // useEffect that updates the income gaps when the projected future income or future income required changes
    useEffect(() => {
        const newIncomeGaps = LTVCalculators.analyzeIncomeGaps(projectedFutureIncome, futureIncomeRequired, original_income_gaps);
        setIncomeGaps(newIncomeGaps);
    }, [projectedFutureIncome, futureIncomeRequired]);

    // useEffect that calculates required income levels for each percentage across future home values
    useEffect(() => {

        const futureIncomeRequirements = LTVCalculators.calculateFutureIncomeRequirements(percentageLevels, futureHomeValues, current_monthly_expenses, interestRatePercent, downPaymentPercent)

        setFutureIncomeRequired(futureIncomeRequirements);
    }, [futureHomeValues]);

    // useEffect that updates the projected future income when the currentIncome or userIncomeGrowthRate changes
    useEffect(() => {
        if (userIncomeGrowthRate) {
            const newProjectedFutureIncome = LTVCalculators.calculate_projected_future_income(highest_income_today_calculated, userIncomeGrowthRate);
            setProjectedFutureIncome(newProjectedFutureIncome);
        }
    }, [userIncomeGrowthRate]);

    // useEffect to know if the user has made changes that we need to display
    useEffect(() => {
        if (currentHomeValue !== originalValues.currentHomeValue ||
            marketGrowthRate !== originalValues.marketGrowthRate ||
            (userIncomeGrowthRate !== null &&
                LTVCalculators.roundForComparison(userIncomeGrowthRate) !== LTVCalculators.roundForComparison(highest_income_level.growth_rate))) {
            if (!hasUserMadeChanges) {
                setHasUserMadeChanges(true);
            }
        } else {
            if (hasUserMadeChanges) {
                setHasUserMadeChanges(false);
            }
        }
    }, [currentHomeValue, marketGrowthRate, userIncomeGrowthRate])


    const stages = [
        {
            id: 1,
            title: "How do ownership costs affect the pool of potential buyers and why do we care?",
            description: "It’s one thing to manage these costs as the current homeowner — but what about future buyers? They’ll need to cover these same expenses, plus a mortgage. As insurance, maintenance, and utility costs rise, the total cost of ownership may outpace what a potential buyer can afford. When that happens, interest in the home can drop, hurting its value."
        },
        {
            id: 2,
            title: "What household income is required to afford this home today?",
            description: "There are many reasons a buyer might be drawn to a home, but financially, the ability to afford it is the baseline requirement. While some buyers may choose to spend more than 30% of their income on housing, doing so often means stretching their budget — and taking on greater financial risk."
        },
        {
            id: 3,
            title: "How does this required income compare to households in the area?",
            description: "As buyers stretch their budgets, the required income drops — but fewer are willing to go that far. Let’s look at the profile of households in this area to see how many can realistically afford this home."
        },
        {
            id: 4,
            title: "Will there be enough buyers to keep this home's market value going up?",
            description: "Home prices go up as a result of competition between potential buyers."
        }
    ]

    const handleNavigate = (direction) => {
        let newStage;
        if (direction === 'next') {
            newStage = currentStage + 1;
        } else if (direction === 'prev') {
            newStage = currentStage - 1;
        }

        if (newStage >= 1 && newStage <= stages.length) {
            setCurrentStage(newStage);

            // Add smooth scrolling
            const container = document.querySelector('.ltv-section-container');
            if (container) {
                container.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }
    };

    const currentStageData = stages.find(stage => stage.id === currentStage);

    // useEffect to log the change to state
    useEffect(() => {
        const GALogEventId = `ltv_market_appeal_step_${currentStage}`;
        handleLoggingGaEventFromProperty(GALogEventId);
    }, [currentStage]);

    const renderStageContent = () => {
        switch (currentStage) {
            case 1:
                return (
                    <LTVAttractivenessAbout
                        currentStageData={currentStageData}
                        incomePercentages={incomePercentages}
                    />
                );
            case 2:
                return (
                    <LTVAttractivenessIncomeRequiredToday
                        currentStageData={currentStageData}
                        originalValues={originalValues}
                        currentHomeValue={currentHomeValue}
                        setCurrentHomeValue={setCurrentHomeValue}
                        interestRatePercent={interestRatePercent}
                        setInterestRatePercent={setInterestRatePercent}
                        downPaymentPercent={downPaymentPercent}
                        setDownPaymentPercent={setDownPaymentPercent}
                        handleReset={handleReset}
                        incomePercentages={incomePercentages}
                    />
                );
            case 3:

                return (
                    <LTVAttractivenessLookingAtMarket
                        currentStageData={currentStageData}
                        originalValues={originalValues}
                        currentHomeValue={currentHomeValue}
                        setCurrentHomeValue={setCurrentHomeValue}
                        interestRatePercent={interestRatePercent}
                        setInterestRatePercent={setInterestRatePercent}
                        downPaymentPercent={downPaymentPercent}
                        setDownPaymentPercent={setDownPaymentPercent}
                        handleReset={handleReset}
                        incomePercentages={incomePercentages}
                        stage3IncomeDisplayArray={stage3_income_display_array}
                        fipsChartData={fipsChartData}
                        allRequiredIncomesAbove200k={allRequiredIncomesAbove200k}
                        incomeHigherInCounty={incomeHigherInCounty}
                        medianIncomeGrowingFasterInCounty={medianIncomeGrowingFasterInCounty}
                        meanIncomeGrowingFasterInCounty={meanIncomeGrowingFasterInCounty}
                    />
                );
            case 4:

                return (
                    <LTVAttractivenessFutureLooking
                        currentStageData={currentStageData}
                        originalValues={originalValues}
                        currentHomeValue={currentHomeValue}
                        setCurrentHomeValue={setCurrentHomeValue}
                        marketGrowthRate={marketGrowthRate}
                        setMarketGrowthRate={setMarketGrowthRate}
                        userIncomeGrowthRate={userIncomeGrowthRate}
                        setUserIncomeGrowthRate={setUserIncomeGrowthRate}
                        projectedFutureIncome={projectedFutureIncome}
                        futureIncomeRequired={futureIncomeRequired}
                        futureHomeValues={futureHomeValues}
                        incomeGaps={incomeGaps}
                        original_income_gaps={original_income_gaps}
                        highest_income_level={highest_income_level}
                        allRequiredIncomesAbove200k={allRequiredIncomesAbove200k}
                        hasUserMadeChanges={hasUserMadeChanges}
                        handleReset={handleReset}
                    />
                );
            default:
                return (
                    <>
                        <p className="ltv-attractiveness-description text-align-center" dangerouslySetInnerHTML={{ __html: currentStageData.description + stage_4_intro_sentence_addition + stage_4_intro_conclusion }} />
                    </>
                );
        }
    };

    return (
        <div className="ltv-section-container">
            <div className="ltv-section-container-header">

                {/* <div className="ltv-type-image">
                    <img src={CreateCloudfrontUrl(ltvInfo.icon)} alt={ltvInfo.title} />
                    </div> */}
                <div className="ltv-section-container-header-text">
                    <span className={`text-align-center`}>{ltvInfo.title}</span>
                </div>
                <button className={`ltv-section-container-header-rating ${ltvInfo.riskLevel}`}>
                    {ltvInfo.riskLevel.toLowerCase() === 'high' ? 'High' : ltvInfo.riskLevel.toLowerCase() === 'medium' ? 'Medium' : 'Low'} Likelihood
                </button>
            </div>
            <div className="ltv-section-container-content">
                {renderStageContent()}
                <nav className="ltv-attractiveness-navigation">
                    {currentStage > 1 && (
                        <button
                            className="ltv-attractiveness-nav-button prev"
                            onClick={() => handleNavigate('prev')}
                        >
                            <ArrowLeft />
                            <span className='text-align-center'>{stages[currentStage - 2].title}</span>
                        </button>
                    )}

                    {currentStage < stages.length && (
                        <button
                            className="ltv-attractiveness-nav-button next"
                            onClick={() => handleNavigate('next')}
                        >
                            <span className='text-align-center'>{stages[currentStage].title}</span>
                            <ArrowRight />
                        </button>
                    )}
                </nav>
            </div>
        </div>
    );
};

export default LTVAttractiveness;